/**
 * Argument parsing and the spawn path for client-intercepted dev chat
 * commands (plan 13 §6). Every command answers in chat: a result, or its usage.
 * A command that swallows bad input silently reads as "the game ignored me".
 */
import { CharacterClass } from '../../shared/CharacterTypes';
import { ServiceRegistry } from '../core/ServiceRegistry';

export interface ChatSink {
  systemMessage(text: string): void;
  errorMessage?(text: string): void;
}

/** `/tp x y`, `/tp x,y` or `/tp x, y` → world point; anything else → null. */
export function parseTeleportArgs(args: string): { x: number; y: number } | null {
  const parts = args.split(/[\s,]+/).filter(Boolean);
  if (parts.length !== 2) return null;
  const [x, y] = parts.map(Number);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

/** Playable classes `/class` accepts (the dev class included). */
export const CLASS_NAMES: readonly string[] = Object.values(CharacterClass);

export function parseClassArg(args: string): string | null {
  const name = args.trim().toLowerCase();
  return CLASS_NAMES.includes(name) ? name : null;
}

/** `/speed <multiplier>`: a positive finite number. */
export function parseSpeedArg(args: string): number | null {
  const value = Number(args.trim());
  return args.trim() !== '' && Number.isFinite(value) && value > 0 ? value : null;
}

export const SPAWN_USAGE = 'Usage: /spawnmob <type> [x y] [lv<N>] — e.g. /spawnmob pig_mom, /spawnmob tuskmire_grunda lv20 (/spawn is the same command)';

/** `/spawnmob …` or `/spawn …` → the argument text; any other message → null. */
export function matchSpawnCommand(text: string): string | null {
  const match = text.trim().match(/^\/(?:spawnmob|spawn)(?:\s+(.*))?$/i);
  return match ? (match[1] ?? '') : null;
}

type RoomLike = { send(type: string, payload: unknown): void };

function defaultRoom(): RoomLike | null {
  const registry = ServiceRegistry.getInstance();
  if (!registry.has('NetworkManager')) return null;
  return registry.get('NetworkManager')?.getRoom?.() ?? null;
}

/**
 * `/spawnmob <type> [x y] [lv<N>]` (and its alias `/spawn`): asks the server to add
 * a mob to the live room (`spawn_mob`). Without coordinates the server plants
 * it beside the player; `lv20` puts a ranked mob's hp and xp on the curve at
 * level 20. Always answers in chat.
 */
export function spawnMobCommand(args: string, chat: ChatSink, getRoom: () => RoomLike | null = defaultRoom): void {
  const error = (text: string) => (chat.errorMessage ?? chat.systemMessage).call(chat, text);
  const words = args.trim().split(/\s+/).filter(Boolean);
  const levelWord = words.findIndex((word, index) => index > 0 && /^(?:lv|level)=?\d+$/i.test(word));
  const level = levelWord > 0 ? Number(words[levelWord].replace(/\D/g, '')) : undefined;
  const parts = words.filter((_, index) => index !== levelWord);
  const type = parts[0];
  if (!type) { error(SPAWN_USAGE); return; }
  const x = parts[1] !== undefined ? Number(parts[1]) : undefined;
  const y = parts[2] !== undefined ? Number(parts[2]) : undefined;
  if ((x !== undefined && !Number.isFinite(x)) || (y !== undefined && !Number.isFinite(y)) || parts.length > 3) {
    error(SPAWN_USAGE);
    return;
  }
  try {
    const room = getRoom();
    if (!room) { error('Network not connected — /spawnmob requires multiplayer mode'); return; }
    room.send('spawn_mob', level === undefined ? { type, x, y } : { type, x, y, level });
    chat.systemMessage(`→ /spawnmob ${type}${x !== undefined ? ` ${x}${y !== undefined ? ` ${y}` : ''}` : ''}${level !== undefined ? ` lv${level}` : ''}`);
  } catch (err) {
    error(`spawnmob failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}
